import { CheckIcon, ChevronRightIcon, CopyIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { PolicyProcessingState } from "../../../state/policies";
import { formatDuration, jobDurationSeconds } from "./documentTime";

/** Newest lines only: a long ingestion can log thousands, and the tail is
 *  what anyone watching is reading. The full count stays visible. */
const VISIBLE_LOGS = 200;

const timeOf = (timestamp: string): string => {
  const parsed = new Date(timestamp).getTime();
  if (Number.isNaN(parsed)) return "--:--:--";
  return new Date(parsed).toLocaleTimeString(undefined, {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * The worker's console for this run, live.
 *
 * Auto-scroll follows the tail only while the reader is already at the
 * bottom — scrolling up to read something is a decision the feed must not
 * undo two seconds later when the next poll lands.
 */
export function ProcessingLogs({
  processing,
  open,
  onToggle,
  now,
}: {
  processing: PolicyProcessingState | null;
  open: boolean;
  onToggle: (open: boolean) => void;
  now: number;
}) {
  const consoleRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  const logs = processing?.logs ?? [];
  const visible = logs.slice(-VISIBLE_LOGS);

  useEffect(() => {
    if (!open || !atBottom) return;
    const node = consoleRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [atBottom, logs.length, open]);

  const duration = formatDuration(jobDurationSeconds(processing, now));

  return (
    <details
      className="group border-t border-border/60 first:border-t-0"
      open={open}
      onToggle={(event) => onToggle((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 hover:bg-accent/50">
        <div className="flex items-center gap-2">
          <ChevronRightIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
          <span className="text-sm font-medium">Processing logs</span>
          {logs.length > 0 ? (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground tabular-nums">
              {visible.length}/{logs.length}
            </span>
          ) : null}
          {processing?.active ? (
            <span className="flex items-center gap-1.5 text-xs text-primary">
              <span className="size-1.5 animate-pulse rounded-full bg-primary" />
              live
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {processing?.active && processing.currentStep ? (
            <span className="hidden max-w-[280px] truncate sm:inline">
              {processing.currentStep}
            </span>
          ) : null}
          <span className="tabular-nums">{duration}</span>
        </div>
      </summary>

      <div className="border-t border-border/60 bg-muted/30">
        <div
          ref={consoleRef}
          className="max-h-[280px] overflow-y-auto font-mono text-[11px] leading-5"
          onScroll={(event) => {
            const node = event.currentTarget;
            setAtBottom(node.scrollTop + node.clientHeight >= node.scrollHeight - 12);
          }}
        >
          {visible.length === 0 ? (
            <p className="p-4 text-muted-foreground">
              {processing?.active
                ? "Waiting for the worker to report…"
                : "This run recorded no logs."}
            </p>
          ) : (
            visible.map((entry, index) => (
              <div
                key={`${entry.timestamp}-${index}`}
                className={
                  entry.level === "error"
                    ? "group/log flex gap-3 bg-destructive/10 px-4 py-0.5"
                    : "group/log flex gap-3 px-4 py-0.5 hover:bg-accent/40"
                }
              >
                <span className="w-16 shrink-0 select-none text-muted-foreground">
                  {timeOf(entry.timestamp)}
                </span>
                <span
                  className={
                    entry.level === "error"
                      ? "flex-1 text-destructive"
                      : entry.level === "warn"
                        ? "flex-1 text-warning-foreground"
                        : "flex-1 text-foreground/80"
                  }
                >
                  {entry.message}
                </span>
                <button
                  type="button"
                  title="Copy line"
                  aria-label="Copy line"
                  className="shrink-0 cursor-pointer text-muted-foreground opacity-0 hover:text-foreground group-hover/log:opacity-100"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(entry.message)
                      .then(() => {
                        setCopied(`${entry.timestamp}-${index}`);
                        setTimeout(() => setCopied(null), 1_500);
                      })
                      .catch(() => setCopied(null));
                  }}
                >
                  {copied === `${entry.timestamp}-${index}` ? (
                    <CheckIcon className="size-3" />
                  ) : (
                    <CopyIcon className="size-3" />
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </details>
  );
}
